/**
 * Loading / Error / Empty state theo quy chuẩn N2NAI (mục 5.3).
 * Dùng khi trang load dữ liệu từ RTK Query để luôn có trạng thái rõ ràng.
 */
import { Box, CircularProgress, Alert, Typography, Button } from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'

interface PageDataStateProps {
  /** Đang tải (isLoading / isFetching) */
  isLoading?: boolean
  /** Alias for `isLoading` (synced source code dùng prop này). */
  loading?: boolean
  /** Lỗi từ RTK Query hoặc message string */
  error?: unknown
  /** Không có dữ liệu (sau khi load xong, list rỗng) */
  empty?: boolean
  /** Message khi empty (dùng khi không có emptyContent) */
  emptyMessage?: string
  /** UI tùy chỉnh khi empty (ưu tiên hơn emptyMessage) */
  emptyContent?: React.ReactNode
  /** Nhãn nút thử lại khi lỗi (mặc định tiếng Anh giữ tương thích cũ) */
  retryLabel?: string
  /** Chuỗi khi không đọc được `detail` từ API */
  errorFallbackMessage?: string
  /** Callback retry khi error */
  onRetry?: () => void
  /** Nội dung khi có dữ liệu (optional khi chỉ dùng loading/error) */
  children?: React.ReactNode
  /** Chiều cao tối thiểu vùng loading (px) */
  minHeight?: number
}

export function PageDataState({
  isLoading,
  loading,
  error,
  empty,
  emptyMessage = 'No data available.',
  emptyContent,
  retryLabel = 'Try again',
  errorFallbackMessage = 'Failed to load data. Please try again later.',
  onRetry,
  children,
  minHeight = 280,
}: PageDataStateProps) {
  const _loading = isLoading ?? loading
  if (_loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 6,
          minHeight,
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    const message = typeof error === 'object' && error !== null && 'data' in error
      ? String((error as { data?: { detail?: string } }).data?.detail ?? errorFallbackMessage)
      : typeof error === 'string'
        ? error
        : errorFallbackMessage
    return (
      <Box sx={{ py: 3 }}>
        <Alert
          severity="error"
          action={
            onRetry ? (
              <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={onRetry}>
                {retryLabel}
              </Button>
            ) : undefined
          }
        >
          {message}
        </Alert>
      </Box>
    )
  }

  if (empty) {
    if (emptyContent) return <>{emptyContent}</>
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
          minHeight,
        }}
      >
        <Typography color="text.secondary">{emptyMessage}</Typography>
      </Box>
    )
  }

  return <>{children ?? null}</>
}

export default PageDataState
